import React, { useEffect, useMemo, useState } from "react";
import StaffLayout from "@/components/StaffLayout";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, Calendar as CalIcon, List, ChevronLeft, ChevronRight } from "lucide-react";

const STATUS_STYLES = {
  planning: "bg-amber-500/10 text-amber-300 border-amber-500/30",
  active: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
  completed: "bg-brand-orange-500/10 text-brand-orange-300 border-primary/30",
  cancelled: "bg-red-500/10 text-red-300 border-red-500/30",
};

export default function AdminCohortes() {
  const { user } = useAuth();
  const superadmin = user?.roles?.includes("superadmin_dcc");
  const [cohorts, setCohorts] = useState([]);
  const [view, setView] = useState("table");
  const [selected, setSelected] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [programs, setPrograms] = useState([]);
  const [tenants, setTenants] = useState([]);

  const load = () => api.get("/admin/cohorts").then((r) => setCohorts(r.data));

  useEffect(() => {
    load();
    api.get("/catalog/programs?limit=200").then((r) => setPrograms(r.data.items || []));
    api.get("/admin/tenants").then((r) => {
      const list = Array.isArray(r.data) ? r.data : (r.data?.items || r.data?.data || []);
      setTenants(list);
    }).catch(() => setTenants([]));
  }, []);

  return (
    <StaffLayout user={user} superadmin={superadmin}>
      <div>
        <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
          <div>
            <p className="text-xs uppercase tracking-widest text-[#F26A21] font-semibold">Cohortes</p>
            <h1 className="mt-2 text-3xl font-bold text-white font-display">Programación de cohorts</h1>
            <p className="mt-2 text-gray-400">Vista tabla o calendario mensual. Click en día para agregar sesión.</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="inline-flex rounded-md overflow-hidden border border-gray-800" role="tablist">
              <button onClick={() => setView("table")} data-testid="cohorts-view-table"
                aria-pressed={view === "table"}
                className={`px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1 ${view === "table" ? "bg-[#F26A21] text-[#0B0B0B]" : "text-gray-300 bg-white/5 hover:bg-white/10"}`}>
                <List className="w-3 h-3" /> Tabla
              </button>
              <button onClick={() => setView("calendar")} data-testid="cohorts-view-calendar"
                aria-pressed={view === "calendar"}
                className={`px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1 ${view === "calendar" ? "bg-[#F26A21] text-[#0B0B0B]" : "text-gray-300 bg-white/5 hover:bg-white/10"}`}>
                <CalIcon className="w-3 h-3" /> Calendario
              </button>
            </div>
            <button onClick={() => setShowNew(true)} data-testid="btn-new-cohort"
              className="inline-flex items-center gap-1 px-4 py-2 rounded-md bg-[#B84A0C] hover:bg-primary text-white text-sm font-semibold">
              <Plus className="w-4 h-4" /> Nueva cohorte
            </button>
          </div>
        </div>

        {view === "table" && (
          <div className="overflow-x-auto border border-gray-800 rounded-xl" data-testid="cohorts-table">
            <table className="w-full text-sm">
              <thead className="bg-[#0F172A] text-xs uppercase tracking-widest text-gray-400">
                <tr>
                  <th className="text-left px-3 py-2">Nombre</th>
                  <th className="text-left px-3 py-2">Programa</th>
                  <th className="text-left px-3 py-2">Tenant</th>
                  <th className="text-left px-3 py-2">Fechas</th>
                  <th className="text-left px-3 py-2">Cupo</th>
                  <th className="text-left px-3 py-2">Sesiones</th>
                  <th className="text-left px-3 py-2">Estado</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {cohorts.length === 0 && (
                  <tr><td colSpan={8} className="px-3 py-6 text-center text-gray-500">Sin cohortes registradas.</td></tr>
                )}
                {cohorts.map((c) => (
                  <tr key={c.id} className="border-t border-gray-800" data-testid={`cohort-row-${c.id}`}>
                    <td className="px-3 py-2 text-white font-semibold">{c.name}</td>
                    <td className="px-3 py-2 text-gray-300 text-xs">{programs.find((p) => p.id === c.program_id)?.title || c.program_id}</td>
                    <td className="px-3 py-2 text-gray-300 text-xs">{tenants.find((t) => t.id === c.tenant_id)?.slug || c.tenant_id}</td>
                    <td className="px-3 py-2 font-mono text-xs text-white">
                      {c.start_date?.slice(0, 10)} → {c.end_date?.slice(0, 10)}
                    </td>
                    <td className="px-3 py-2 font-mono text-white">{c.enrolled_count || 0}/{c.capacity || 0}</td>
                    <td className="px-3 py-2 font-mono text-white">{(c.sessions || []).length}</td>
                    <td className="px-3 py-2">
                      <span className={`text-[10px] px-2 py-0.5 rounded uppercase font-semibold border ${STATUS_STYLES[c.status] || STATUS_STYLES.planning}`}>{c.status}</span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button onClick={() => setSelected(c)} data-testid={`cohort-open-${c.id}`}
                        className="text-xs text-[#93C5FD] hover:text-white">Ver</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {view === "calendar" && (
          <CalendarMonth cohorts={cohorts} onOpenSession={(cid) => {
            const c = cohorts.find((x) => x.id === cid); if (c) setSelected(c);
          }} />
        )}
      </div>

      {showNew && (
        <NewCohortModal programs={programs} tenants={tenants} onClose={() => { setShowNew(false); load(); }} />
      )}
      {selected && (
        <CohortDetail cohort={selected} programs={programs} onClose={() => { setSelected(null); load(); }} />
      )}
    </StaffLayout>
  );
}

function NewCohortModal({ programs, tenants, onClose }) {
  const [form, setForm] = useState({
    program_id: "", tenant_id: "", name: "",
    start_date: new Date().toISOString().slice(0, 10),
    end_date: new Date(Date.now() + 90 * 86400000).toISOString().slice(0, 10),
    capacity: 30, status: "planning",
  });

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/cohorts", form);
      toast.success("Cohorte creada");
      onClose();
    } catch (er) { toast.error(formatApiError(er.response?.data?.detail)); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} data-testid="new-cohort-modal"
        className="w-full max-w-md bg-[#111827] border border-gray-800 rounded-xl p-6 space-y-3">
        <div className="flex justify-between items-start">
          <h3 className="text-xl font-semibold text-white">Nueva cohorte</h3>
          <button type="button" onClick={onClose} aria-label="Cerrar" className="text-gray-400"><X /></button>
        </div>
        <FieldSelect label="Programa" value={form.program_id} on={(v) => setForm({ ...form, program_id: v })} testid="nc-program"
          options={programs.map((p) => ({ value: p.id, label: `${p.code || "—"} · ${p.title}` }))} req />
        <FieldSelect label="Tenant" value={form.tenant_id} on={(v) => setForm({ ...form, tenant_id: v })} testid="nc-tenant"
          options={tenants.map((t) => ({ value: t.id, label: `${t.slug} · ${t.name}` }))} req />
        <Field label="Nombre" val={form.name} on={(v) => setForm({ ...form, name: v })} req testid="nc-name" />
        <div className="grid grid-cols-2 gap-3">
          <Field label="Inicio" type="date" val={form.start_date} on={(v) => setForm({ ...form, start_date: v })} testid="nc-start" />
          <Field label="Fin" type="date" val={form.end_date} on={(v) => setForm({ ...form, end_date: v })} testid="nc-end" />
        </div>
        <Field label="Cupo" type="number" val={form.capacity} on={(v) => setForm({ ...form, capacity: parseInt(v, 10) || 0 })} testid="nc-capacity" />
        <button type="submit" data-testid="nc-submit"
          className="w-full py-2.5 rounded-md bg-[#B84A0C] hover:bg-primary text-white font-semibold">
          Crear cohorte
        </button>
      </form>
    </div>
  );
}

function CohortDetail({ cohort, programs, onClose }) {
  const [c, setC] = useState(cohort);
  const [newSession, setNewSession] = useState({
    title: "", scheduled_at: new Date().toISOString().slice(0, 16),
    duration_min: 60, zoom_url: "",
  });
  const [enrollEmails, setEnrollEmails] = useState("");

  const addSession = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post(`/admin/cohorts/${c.id}/sessions`, newSession);
      setC({ ...c, sessions: [...(c.sessions || []), data.session] });
      toast.success("Sesión agregada");
    } catch (er) { toast.error(formatApiError(er.response?.data?.detail)); }
  };

  const removeSession = async (sid) => {
    try {
      await api.delete(`/admin/cohorts/${c.id}/sessions/${sid}`);
      setC({ ...c, sessions: (c.sessions || []).filter((s) => s.id !== sid) });
    } catch (er) { toast.error(formatApiError(er.response?.data?.detail)); }
  };

  const enroll = async () => {
    const emails = enrollEmails.split(/[\s,]+/).filter(Boolean);
    if (emails.length === 0) return toast.error("Ingresa al menos un correo");
    try {
      const { data } = await api.post(`/admin/cohorts/${c.id}/enrollments`, { emails });
      toast.success(`${data.enrolled} enrolados · ${data.skipped.length} omitidos`);
      setEnrollEmails("");
    } catch (er) { toast.error(formatApiError(er.response?.data?.detail)); }
  };

  const program = programs.find((p) => p.id === c.program_id);

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} data-testid="cohort-detail-modal"
        className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-[#111827] border border-gray-800 rounded-xl p-6 space-y-4">
        <div className="flex justify-between items-start">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-[#F26A21]">Cohorte</p>
            <h3 className="text-xl font-bold text-white">{c.name}</h3>
            <p className="text-xs text-gray-400 mt-1">{program?.title}</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Cerrar" className="text-gray-400"><X /></button>
        </div>

        <section>
          <p className="text-sm font-semibold text-white mb-2">Sesiones ({(c.sessions || []).length})</p>
          <div className="space-y-2">
            {(c.sessions || []).map((s) => (
              <div key={s.id} className="p-3 border border-gray-800 rounded flex items-center justify-between" data-testid={`session-${s.id}`}>
                <div>
                  <p className="text-white text-sm">{s.title || "(sin título)"}</p>
                  <p className="text-xs text-gray-400 font-mono">{new Date(s.scheduled_at).toLocaleString("es-MX")} · {s.duration_min} min</p>
                </div>
                <button onClick={() => removeSession(s.id)} aria-label="Eliminar sesión" className="text-red-400 text-xs">Eliminar</button>
              </div>
            ))}
          </div>
          <form onSubmit={addSession} className="mt-3 space-y-2 p-3 bg-[#0B0F14] border border-gray-800 rounded">
            <Field label="Título de sesión" val={newSession.title} on={(v) => setNewSession({ ...newSession, title: v })} testid="ns-title" />
            <div className="grid grid-cols-2 gap-2">
              <Field label="Fecha y hora" type="datetime-local" val={newSession.scheduled_at} on={(v) => setNewSession({ ...newSession, scheduled_at: v })} testid="ns-when" />
              <Field label="Duración (min)" type="number" val={newSession.duration_min} on={(v) => setNewSession({ ...newSession, duration_min: parseInt(v, 10) || 60 })} testid="ns-duration" />
            </div>
            <Field label="Zoom URL (opcional)" val={newSession.zoom_url} on={(v) => setNewSession({ ...newSession, zoom_url: v })} testid="ns-zoom" />
            <button type="submit" data-testid="ns-add-btn"
              className="w-full py-1.5 text-xs rounded bg-[#B84A0C] hover:bg-primary text-white font-semibold">
              Agregar sesión
            </button>
          </form>
        </section>

        <section>
          <p className="text-sm font-semibold text-white mb-2">Enrolar alumnos (por correo)</p>
          <textarea rows={2} value={enrollEmails} onChange={(e) => setEnrollEmails(e.target.value)}
            placeholder="alumno1@banco.mx, alumno2@banco.mx"
            data-testid="enroll-emails"
            className="w-full bg-[#0B0F14] border border-gray-800 rounded px-2 py-1 text-xs text-white" />
          <button onClick={enroll} data-testid="enroll-submit"
            className="mt-2 px-3 py-1.5 text-xs rounded bg-emerald-500 hover:bg-emerald-600 text-white font-semibold">
            Enrolar
          </button>
        </section>
      </div>
    </div>
  );
}

function CalendarMonth({ cohorts, onOpenSession }) {
  const [cursor, setCursor] = useState(() => {
    const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1);
  });

  const grid = useMemo(() => buildMonthGrid(cursor), [cursor]);
  const events = useMemo(() => {
    const arr = [];
    cohorts.forEach((c) => {
      (c.sessions || []).forEach((s) => {
        if (!s.scheduled_at) return;
        arr.push({ cohort_id: c.id, cohort_name: c.name, ...s });
      });
    });
    return arr;
  }, [cohorts]);

  const monthLabel = cursor.toLocaleDateString("es-MX", { year: "numeric", month: "long" });

  return (
    <div className="p-4 bg-[#111827] border border-gray-800 rounded-xl" data-testid="cohorts-calendar">
      <div className="flex items-center justify-between mb-4">
        <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}
          aria-label="Mes anterior" className="p-2 hover:bg-white/5 rounded"><ChevronLeft className="w-4 h-4 text-white" /></button>
        <p className="text-white font-semibold uppercase tracking-widest text-sm">{monthLabel}</p>
        <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}
          aria-label="Mes siguiente" className="p-2 hover:bg-white/5 rounded"><ChevronRight className="w-4 h-4 text-white" /></button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-[10px] uppercase tracking-widest text-gray-500 mb-2">
        {["L", "M", "M", "J", "V", "S", "D"].map((d, i) => (<div key={i} className="text-center">{d}</div>))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {grid.map((day, i) => {
          const key = day ? day.toISOString().slice(0, 10) : `blank-${i}`;
          const dayEvents = day ? events.filter((e) => e.scheduled_at.startsWith(key)) : [];
          return (
            <div key={key} className={`min-h-24 p-1 rounded border ${day ? "bg-[#0B0F14] border-gray-800" : "border-transparent"}`}>
              {day && (<>
                <p className="text-[10px] text-gray-500 font-mono">{day.getDate()}</p>
                {dayEvents.slice(0, 3).map((e) => (
                  <button key={e.id} onClick={() => onOpenSession(e.cohort_id)}
                    data-testid={`cal-event-${e.id}`}
                    className="mt-1 w-full text-left text-[9px] px-1 py-0.5 rounded bg-[#F26A21]/20 border border-[#F26A21]/40 text-[#F26A21] truncate hover:bg-[#F26A21]/30">
                    {e.title || e.cohort_name}
                  </button>
                ))}
                {dayEvents.length > 3 && (<p className="text-[9px] text-gray-500 mt-0.5">+{dayEvents.length - 3}</p>)}
              </>)}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function buildMonthGrid(monthStart) {
  const days = [];
  const firstDay = (monthStart.getDay() + 6) % 7; // convert to Mon=0
  for (let i = 0; i < firstDay; i += 1) days.push(null);
  const nextMonth = new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 1);
  const cursor = new Date(monthStart);
  while (cursor < nextMonth) {
    days.push(new Date(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  while (days.length % 7 !== 0) days.push(null);
  return days;
}

function Field({ label, val, on, type = "text", testid, req }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-widest text-gray-400">{label}{req && " *"}</span>
      <input type={type} required={req} value={val || ""} onChange={(e) => on(e.target.value)}
        data-testid={testid}
        className="w-full mt-1 bg-[#0B0F14] border border-gray-800 rounded-md px-3 py-2 text-sm text-white" />
    </label>
  );
}

function FieldSelect({ label, value, on, testid, options, req }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-widest text-gray-400">{label}{req && " *"}</span>
      <select required={req} value={value} onChange={(e) => on(e.target.value)}
        data-testid={testid}
        className="w-full mt-1 bg-[#0B0F14] border border-gray-800 rounded-md px-3 py-2 text-sm text-white">
        <option value="">Selecciona...</option>
        {options.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
      </select>
    </label>
  );
}
